/**
 * Event Timeline view — Faz 8.1.
 *
 * Paginated, filterable list of archived kernel events. Each row
 * links to the per-event detail (`/events/:tickId`) and to the
 * lineage view via the event's `correlationId`. Filters compose via
 * URL search params so back/forward keep the timeline state.
 *
 * Filter fields are aligned with the canonical
 * `@risk-alarm/cockpit-api-contract` `ReplayEventsQuery` schema —
 * `eventKind` and `symbol`. The earlier `correlationId` text input
 * was removed because the server never consumed it (silent no-op,
 * A1 P0 fix); operators now follow the per-row correlation link
 * into the dedicated `/lineage/:correlationId` page for causal
 * drill-down.
 */

import { useMemo, useState, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError } from "../api/client.js";
import { fetchEvents, queryKeys } from "../api/queries.js";

const PAGE_SIZE = 50;

export const EventTimelineView = (): ReactElement => {
  const [page, setPage] = useState<number>(0);
  const [eventKindFilter, setEventKindFilter] = useState<string>("");
  const [symbolFilter, setSymbolFilter] = useState<string>("");

  const offset = page * PAGE_SIZE;
  const fetchArgs = {
    limit: PAGE_SIZE,
    offset,
    eventKind: eventKindFilter,
    symbol: symbolFilter,
  };
  const query = useQuery({
    queryKey: queryKeys.events(fetchArgs),
    queryFn: () => fetchEvents(fetchArgs),
  });

  const events = useMemo(() => query.data?.events ?? [], [query.data]);
  const hasMore = query.data?.hasMore ?? false;

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end gap-3">
        <h2 className="text-lg font-semibold">Event Timeline</h2>
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          <span>Event türü</span>
          <input
            aria-label="Filter by eventKind"
            placeholder="eventKind (e.g. safety.freeze.activated)"
            value={eventKindFilter}
            onChange={(e) => {
              setEventKindFilter(e.target.value);
              setPage(0);
            }}
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-400">
          <span>Sembol</span>
          <input
            aria-label="Filter by symbol"
            placeholder="BTCUSDT"
            value={symbolFilter}
            onChange={(e) => {
              setSymbolFilter(e.target.value);
              setPage(0);
            }}
            className="rounded border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
          />
        </label>
        <p className="ml-auto max-w-xs text-xs text-slate-500">
          Correlation ID için bir satırın correlationId hücresine tıklayın ya
          da doğrudan{" "}
          <code className="rounded bg-slate-800 px-1 py-0.5 text-slate-300">
            /lineage/:id
          </code>{" "}
          sayfasına gidin.
        </p>
      </header>

      {query.isLoading && <p data-testid="loading">Loading…</p>}
      {query.isError && (
        <p className="text-rose-400" data-testid="error">
          {query.error instanceof ApiError
            ? `${query.error.server.kind}: ${query.error.server.message}`
            : "Unknown error"}
        </p>
      )}

      {query.data && (
        <table className="w-full table-auto border-collapse text-sm">
          <thead className="bg-slate-900/70 text-xs uppercase tracking-wide text-slate-300">
            <tr>
              <th className="px-3 py-2 text-left">tickId</th>
              <th className="px-3 py-2 text-left">ts</th>
              <th className="px-3 py-2 text-left">kind</th>
              <th className="px-3 py-2 text-left">correlationId</th>
              <th className="px-3 py-2 text-left">configHash</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {events.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-slate-500" colSpan={5}>
                  No events match the current filter.
                </td>
              </tr>
            )}
            {events.map((row) => (
              <tr key={String(row.event.tickId)} className="hover:bg-slate-900/50">
                <td className="px-3 py-1 font-mono">
                  <Link
                    to={`/events/${String(row.event.tickId)}`}
                    className="text-sky-400 hover:underline"
                  >
                    {String(row.event.tickId)}
                  </Link>
                </td>
                <td className="px-3 py-1 font-mono">{String(row.event.ts)}</td>
                <td className="px-3 py-1 font-mono">{row.event.kind}</td>
                <td className="px-3 py-1 font-mono">
                  <Link
                    to={`/lineage/${encodeURIComponent(row.event.correlationId)}`}
                    className="text-emerald-400 hover:underline"
                  >
                    {row.event.correlationId}
                  </Link>
                </td>
                <td className="px-3 py-1 font-mono text-xs text-slate-400">
                  {row.configHash.slice(0, 12)}…
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <footer className="flex items-center gap-3 text-sm">
        <button
          type="button"
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          className="rounded border border-slate-700 px-3 py-1 disabled:opacity-40"
        >
          ← Prev
        </button>
        <span className="text-slate-400">Page {page + 1}</span>
        <button
          type="button"
          disabled={!hasMore}
          onClick={() => setPage((p) => p + 1)}
          className="rounded border border-slate-700 px-3 py-1 disabled:opacity-40"
        >
          Next →
        </button>
      </footer>
    </section>
  );
};
